import React, { useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { Users, Repeat, Truck, DollarSign, Map, UserCog } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { KpiCard } from "../components/shared/KpiCard";
import { ErrorState } from "../components/shared/ErrorState";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Skeleton } from "../components/ui/skeleton";
import { CashCollectionTab } from "../components/reports/CashCollectionTab";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { formatCurrency, formatNumber, todayLocal } from "../lib/utils";
import { useTaxConfig, computeGst } from "../lib/tax";
import { subscriptionStatusMeta } from "../lib/statusMaps";
import { useListState } from "../hooks/useListState";
import brandColors from "../brandColors";

const CHART_COLORS = [
  brandColors.PRIMARY, brandColors.ACCENT, "#2563EB", "#DC2626", "#7C3AED",
  "#0D9488", "#EA580C", "#4B5563", "#DB2777", "#65A30D",
];

export default function Dashboard() {
  const [activeTab, setActiveTab] = useListState("dashboard.activeTab", "overview");
  const taxCfg = useTaxConfig();

  const dashboardQuery = useQuery({
    queryKey: ["dashboard-summary"],
    queryFn: async () => {
      const today = todayLocal();
      const [summaryRes, customersRes, financialRes, driversRes] = await Promise.all([
        apiClient.get("/reports/dashboard-summary"),
        apiClient.get("/reports/customers-subscriptions"),
        apiClient.get("/reports/financial"),
        apiClient.get("/drivers", { params: { limit: 1 } }),
      ]);
      let deliveryToday = null;
      try {
        const { data } = await apiClient.get("/reports/delivery-status-totals", {
          params: { date_from: today, date_to: today },
        });
        deliveryToday = data;
      } catch {
        deliveryToday = null;
      }
      return {
        summary: summaryRes.data,
        customers: customersRes.data,
        financial: financialRes.data,
        drivers: driversRes.data,
        deliveryToday,
      };
    },
  });

  const state = {
    loading: dashboardQuery.isPending,
    error: dashboardQuery.isError ? getApiErrorMessage(dashboardQuery.error, "Could not load dashboard data.") : null,
    data: dashboardQuery.data || null,
  };
  const load = () => dashboardQuery.refetch();

  const { summary, customers, financial, drivers, deliveryToday } = state.data || {};

  const revenueChartData = useMemo(
    () => (summary?.revenue_last_6_months || []).map((r) => ({ label: r.label, revenue: r.revenue || 0 })),
    [summary]
  );
  const deliveryTrendData = useMemo(
    () =>
      (summary?.delivery_trend_last_14_days || []).map((d) => ({
        date: d.date?.slice(5),
        Total: d.total,
        Delivered: d.done,
      })),
    [summary]
  );
  const planDistribution = useMemo(
    () => (summary?.active_subscriptions_by_plan || []).map((p) => ({ name: p.plan_name || `Plan #${p.plan_id}`, value: p.count })),
    [summary]
  );

  if (state.error) {
    return (
      <div data-testid="dashboard-page">
        <PageHeader title="Dashboard" description="Operational overview across customers, subscriptions and deliveries." />
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="dashboard-tabs">
            <TabsTrigger value="overview" data-testid="dashboard-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="cash-collection" data-testid="dashboard-tab-cash-collection">Cash Collection</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <ErrorState message={state.error} onRetry={load} />
          </TabsContent>
          <TabsContent value="cash-collection">
            <CashCollectionTab />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  const loading = state.loading;
  const statusCounts = summary?.subscription_status_counts;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Operational overview across customers, subscriptions and deliveries."
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="overview" data-testid="dashboard-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="cash-collection" data-testid="dashboard-tab-cash-collection">Cash Collection</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="dashboard-kpi-grid">
        <KpiCard
          testId="kpi-customers"
          title="Total Customers"
          icon={Users}
          loading={loading}
          value={!loading && formatNumber(customers?.total_customers)}
          hint={!loading && `${formatNumber(customers?.total_subscriptions)} subscriptions on file`}
        />
        <KpiCard
          testId="kpi-active-subscriptions"
          title="Active Subscriptions"
          icon={Repeat}
          loading={loading}
          value={!loading && formatNumber(statusCounts?.active)}
          hint={
            !loading &&
            `${formatNumber(statusCounts?.pending)} pending · ${formatNumber(statusCounts?.deactivated)} deactivated · ${formatNumber(statusCounts?.expired)} expired`
          }
        />
        <KpiCard
          testId="kpi-deliveries-today"
          title="Today's Deliveries"
          icon={Truck}
          loading={loading}
          value={!loading && formatNumber(deliveryToday?.total ?? summary?.delivery_today?.total)}
          hint={
            !loading &&
            (deliveryToday
              ? `${formatNumber(deliveryToday.delivered)} delivered · ${formatNumber(deliveryToday.pending)} pending · ${formatNumber(deliveryToday.not_delivered)} not delivered`
              : `${formatNumber(summary?.delivery_today?.done)} delivered so far`)
          }
        />
        <KpiCard
          testId="kpi-revenue"
          title="Approved Revenue"
          icon={DollarSign}
          accent
          loading={loading}
          value={!loading && formatCurrency(financial?.total_approved_amount)}
          hint={
            !loading &&
            (financial?.reconciliation_mismatches
              ? `${financial.reconciliation_mismatches} payment mismatch(es) found`
              : `incl. GST ${formatCurrency(computeGst(financial?.total_approved_amount, taxCfg).total)} · ${formatCurrency(financial?.total_pending_amount)} pending`)
          }
        />
        <KpiCard
          testId="kpi-routes"
          title="Total Routes"
          icon={Map}
          loading={loading}
          value={!loading && formatNumber(summary?.route_count)}
        />
        <KpiCard
          testId="kpi-drivers"
          title="Drivers on File"
          icon={UserCog}
          loading={loading}
          value={!loading && formatNumber(drivers?.total)}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2" data-testid="chart-revenue-trend">
          <CardHeader>
            <CardTitle>Recorded payments — last 6 months</CardTitle>
            <CardDescription>
              All payment amounts on file by month (approved + pending) — a different figure than the
              "Approved Revenue" KPI above, which counts only approved payments to date.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : revenueChartData.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E4DF" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#4B5563" }} axisLine={{ stroke: "#E2E4DF" }} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#4B5563" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                  />
                  <RTooltip
                    contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E4DF", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => formatCurrency(v)}
                  />
                  <Bar dataKey="revenue" fill={brandColors.PRIMARY} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-16 text-center text-sm text-text-muted">No revenue data yet.</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="chart-plan-distribution">
          <CardHeader>
            <CardTitle>Active subscriptions by plan</CardTitle>
            <CardDescription>Current distribution across plans.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : planDistribution.length ? (
              <div className="flex flex-col gap-4">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={planDistribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {planDistribution.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RTooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E4DF", borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <ul className="max-h-40 space-y-1.5 overflow-y-auto pr-1 text-xs" data-testid="plan-distribution-legend">
                  {planDistribution.map((p, i) => (
                    <li key={p.name} className="flex items-center justify-between gap-3">
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2 w-2 flex-shrink-0 rounded-full"
                          style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                        />
                        <span className="truncate text-text-muted" title={p.name}>{p.name}</span>
                      </span>
                      <span className="flex-shrink-0 font-medium text-text-main">{p.value}</span>
                    </li>
                  ))}
                </ul>
                {planDistribution.length > 5 && (
                  <p className="-mt-1 text-center text-[11px] text-text-muted">Scroll for all {planDistribution.length} plans</p>
                )}
              </div>
            ) : (
              <p className="py-16 text-center text-sm text-text-muted">No active subscriptions yet.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card className="xl:col-span-2" data-testid="chart-delivery-trend">
          <CardHeader>
            <CardTitle>Deliveries — last 14 days</CardTitle>
            <CardDescription>Total scheduled vs. delivered.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : deliveryTrendData.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={deliveryTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E4DF" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#4B5563" }} axisLine={{ stroke: "#E2E4DF" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#4B5563" }} axisLine={false} tickLine={false} />
                  <RTooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E4DF", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Total" stroke="#6B7280" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Delivered" stroke={brandColors.PRIMARY} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-16 text-center text-sm text-text-muted">No delivery history yet.</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="subscription-status-breakdown">
          <CardHeader>
            <CardTitle>Subscription status</CardTitle>
            <CardDescription>Snapshot across all customers.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <Skeleton className="h-48 w-full" />
            ) : (
              [1, 2, 3, 4].map((code) => {
                const meta = subscriptionStatusMeta(code);
                const value = { 1: statusCounts?.active, 2: statusCounts?.pending, 3: statusCounts?.deactivated, 4: statusCounts?.expired }[code];
                return (
                  <div key={code} className="flex items-center justify-between">
                    <StatusBadge label={meta.label} variant={meta.variant} />
                    <span className="font-heading text-sm font-semibold text-text-main">{formatNumber(value)}</span>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="cash-collection">
          <CashCollectionTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
